import { createFeatureSelector, createSelector } from '@ngrx/store';
import { StudyDataState } from '../../../reducers';
import { selectStudyDatabaseNames } from '../../../study.selectors';

import { selectAllComparables } from '../../../comparables.selectors';

export const selectStudyDataState = createFeatureSelector<StudyDataState>('selectedStudy');

export const selectSlideOutData = createSelector(selectStudyDataState, (data) => data.slideOut);

export const selectDrillDownDetailsData = createSelector(selectSlideOutData, (data) => data.drillDownDetails);

export const selectLoadingFlag = createSelector(selectDrillDownDetailsData, (data) => data.isLoading);

export const selectSelectedComparableId = createSelector(selectDrillDownDetailsData, (data) => data.selectedComparableId);

export const selectComparableIdsByPageNumber = createSelector(selectDrillDownDetailsData, (data) =>
  !data.externalData || !data.externalData.idsByPage ? undefined : data.externalData.idsByPage
);

export const selectNavigationPage = createSelector(selectDrillDownDetailsData, (data) => (!data.navigation ? undefined : data.navigation.page));

export const selectNavigationIndex = createSelector(selectDrillDownDetailsData, (data) => (!data.navigation ? undefined : data.navigation.index));

export const selectComparableIdByPageAndIndex = createSelector(
  selectComparableIdsByPageNumber,
  selectNavigationPage,
  selectNavigationIndex,
  (pages, page, index) => (!pages || !pages[page] || !pages[page][index] ? undefined : pages[page][index])
);

export const selectCompanyName = createSelector(selectComparableIdByPageAndIndex, selectAllComparables, (id, comparables) =>
  !id ? undefined : comparables[id].companyName
);

export const selectCompanyDatabaseId = createSelector(selectComparableIdByPageAndIndex, selectAllComparables, (id, comparables) =>
  !id || !comparables || !comparables[id] ? undefined : comparables[id].databaseId
);

export const selectNavigationData = createSelector(selectNavigationPage, selectNavigationIndex, (page, index) => {
  return { page, index };
});

export const selectExternalData = createSelector(selectDrillDownDetailsData, (data) =>
  !data.externalData ? undefined : { rows: data.externalData.rows, count: data.externalData.count }
);

export const selectDrillDownPageDataRequestIndication = createSelector(
  selectDrillDownDetailsData,
  selectNavigationPage,
  selectNavigationIndex,
  (data, page, index) =>
    !data || page === undefined || index === undefined ? undefined : { isPageDataRequested: data.isPageDataRequested, page, index }
);

export const selectComparableSelectionData = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded
    ? undefined
    : {
        currencyId: comparables[id].financialData.currencyData.studyCurrencyId,
        isConsolidated: comparables[id].financialData.isConsolidated,
      }
);

export const selectInitLoad = createSelector(selectDrillDownDetailsData, (data) => data.initLoad);

export const selectSelectionData = createSelector(selectDrillDownDetailsData, (data) => data.selectionData);

export const hasSelectionData = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) => (!selection[id] ? false : true));

export const selectSelectedCompanyData = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id]
    ? undefined
    : {
        comparableId: comparables[id].comparableId,
        uniqueId: comparables[id].groupLinkId === null ? comparables[id].comparableId : comparables[id].groupLinkId,
      }
);

export const hasDrillDownDetailsLoaded = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded ? false : true
);

export const selectCompanyDatabases = createSelector(
  selectAllComparables,
  selectSelectedComparableId,
  selectStudyDatabaseNames,
  (comparables, id, dbNames) =>
    !comparables[id] || !comparables[id].databases
      ? undefined
      : comparables[id].databases.map((id) => {
          return { id, name: dbNames[id] };
        })
);

export const selectCompanyDatabasesCount = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].databases ? undefined : comparables[id].databases.length
);

export const selectCompanyDatabaseName = createSelector(selectCompanyDatabaseId, selectStudyDatabaseNames, (id, dbNames) =>
  !id || !dbNames ? undefined : dbNames[id]
);

export const selectIndustryDescriptions = createSelector(
  selectAllComparables,
  selectSelectionData,
  selectSelectedComparableId,
  (comparables, selection, id) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? undefined
      : comparables[id].industryDescriptions[selection[id].industryDescriptions.currentDatabaseId]
);

export const selectIndustryDescriptionsSelectedDatabaseId = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].industryDescriptions.selectedDatabaseId
);

export const hasIndustryDescriptionsLoaded = createSelector(
  selectAllComparables,
  selectSelectionData,
  selectSelectedComparableId,
  (comparables, selection, id) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? false
      : !!comparables[id].industryDescriptions[selection[id].industryDescriptions.selectedDatabaseId]
);

export const selectCompanyInfo = createSelector(selectAllComparables, selectSelectionData, selectSelectedComparableId, (comparables, selection, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
    ? undefined
    : comparables[id].companyInfo[selection[id].companyInfo.currentDatabaseId]
);

export const selectCompanyInfoSelectedDatabaseId = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].companyInfo.selectedDatabaseId
);

export const hasCompanyInfoLoaded = createSelector(
  selectAllComparables,
  selectSelectionData,
  selectSelectedComparableId,
  (comparables, selection, id) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? false
      : !!comparables[id].companyInfo[selection[id].companyInfo.selectedDatabaseId]
);

export const selectBusinessDescription = createSelector(
  selectAllComparables,
  selectSelectionData,
  selectSelectedComparableId,
  (comparables, selection, id) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? undefined
      : comparables[id].businessDescriptions[selection[id].businessDescriptions.currentDatabaseId]
);

export const selectBusinessDescriptionSelectedDatabaseId = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].businessDescriptions.selectedDatabaseId
);

export const hasBusinessDescriptionLoaded = createSelector(
  selectAllComparables,
  selectSelectionData,
  selectSelectedComparableId,
  (comparables, selection, id) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? false
      : !!comparables[id].businessDescriptions[selection[id].businessDescriptions.selectedDatabaseId]
);

export const selectSubsidiaries = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded ? undefined : comparables[id].subsidiaries
);

export const selectShareHolders = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded ? undefined : comparables[id].shareHolders
);

export const selectFinancialData = createSelector(
  selectAllComparables,
  selectSelectedComparableId,
  selectSelectionData,
  (comparables, id, selection) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? undefined
      : {
          loading: selection[id].financialData.isLoading,
          item: comparables[id].financialData.items[
            selection[id].financialData.currentDatabaseId +
              selection[id].financialData.currentCurrencyId +
              +selection[id].financialData.currentConsolidated
          ],
          financialsRange: comparables[id].financialData.financialsRange,
        }
);

export const selectFinancialDataCurrentDatabaseId = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].financialData.currentDatabaseId
);

export const selectFinancialDataSelectedDatabaseId = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].financialData.selectedDatabaseId
);

export const selectFinancialDataSelectedCurrencyId = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].financialData.selectedCurrencyId
);

export const selectFinancialDataSelectedConsolidated = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id] ? undefined : selection[id].financialData.selectedConsolidated
);

export const hasDatabaseFinancialDataLoaded = createSelector(
  selectAllComparables,
  selectSelectedComparableId,
  selectSelectionData,
  (comparables, id, selection) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? false
      : !!comparables[id].financialData.items[
          selection[id].financialData.selectedDatabaseId +
            selection[id].financialData.currentCurrencyId +
            +selection[id].financialData.currentConsolidated
        ]
);

export const hasCurrencyFinancialDataLoaded = createSelector(
  selectAllComparables,
  selectSelectedComparableId,
  selectSelectionData,
  (comparables, id, selection) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? false
      : !!comparables[id].financialData.items[
          selection[id].financialData.currentDatabaseId +
            selection[id].financialData.selectedCurrencyId +
            +selection[id].financialData.currentConsolidated
        ]
);

export const hasConsolidatedFinancialDataLoaded = createSelector(
  selectAllComparables,
  selectSelectedComparableId,
  selectSelectionData,
  (comparables, id, selection) =>
    !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded || !selection[id]
      ? false
      : !!comparables[id].financialData.items[
          selection[id].financialData.currentDatabaseId +
            selection[id].financialData.currentCurrencyId +
            +selection[id].financialData.selectedConsolidated
        ]
);

export const hasFinancialInitialLoaded = createSelector(selectSelectionData, selectSelectedComparableId, (selection, id) =>
  !selection[id]
    ? false
    : selection[id].financialData.currentDatabaseId !== undefined &&
      selection[id].financialData.currentCurrencyId !== undefined &&
      selection[id].financialData.currentConsolidated !== undefined
);

export const selectFinancialYearAlignmentData = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded
    ? undefined
    : {
        alignmentMethodId: comparables[id].financialData.alignmentMethodId,
        monthsBack: comparables[id].financialData.monthsBack,
        testedPartyFiscalYearEnd: comparables[id].financialData.testedPartyFiscalYearEnd,
      }
);

export const selectFinancialCurrencyData = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded
    ? undefined
    : {
        currencyUnit: comparables[id].financialData.currencyData.currencyUnit,
        studyCurrencyId: comparables[id].financialData.currencyData.studyCurrencyId,
        comparableCurrencyId: comparables[id].financialData.currencyData.comparableCurrencyId,
        currencies: comparables[id].financialData.currencyData.currencies,
      }
);

export const selectHasSingleTypeOfFinancialsFlag = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded ? undefined : comparables[id].financialData.hasSingleTypeOfFinancials
);

export const selectFinancialCategoryNames = createSelector(selectAllComparables, selectSelectedComparableId, (comparables, id) =>
  !comparables[id] || !comparables[id].hasDrillDownDetailsLoaded ? [] : comparables[id].financialData.categoryNames
);
